import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Building2, Users2, Package, Warehouse, ShoppingBag,
  CreditCard, ScrollText, Sparkles, Settings, UserCog, Boxes,
  Truck, RotateCcw, Zap, Store, Activity, Radio, Wand2, Layers,
  Route, ShieldAlert, LineChart, Landmark, Code2, Wallet, Mail, GraduationCap,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import OmsLogo from "@/components/OmsLogo";

const NAV = [
  { to: "/command", label: "Command Center", icon: Radio, testid: "nav-command" },
  { to: "/dashboard", label: "Overview", icon: LayoutDashboard, testid: "nav-overview" },
  { to: "/orders", label: "Orders", icon: ScrollText, testid: "nav-orders" },
  { to: "/products", label: "Products", icon: Package, testid: "nav-products" },
  { to: "/inventory", label: "Inventory", icon: Boxes, testid: "nav-inventory" },
  { to: "/wms", label: "Warehouse Ops", icon: Layers, testid: "nav-wms" },
  { to: "/fulfillment", label: "AI Fulfillment", icon: Route, testid: "nav-fulfillment" },
  { to: "/couriers", label: "Courier AI", icon: Truck, testid: "nav-couriers" },
  { to: "/shipping", label: "Shipping", icon: Truck, testid: "nav-shipping" },
  { to: "/returns", label: "Returns", icon: RotateCcw, testid: "nav-returns" },
];

const OPS_NAV = [
  { to: "/automation", label: "Automation", icon: Zap, testid: "nav-automation" },
  { to: "/workflow-builder", label: "Workflow Builder", icon: Wand2, testid: "nav-workflow-builder" },
  { to: "/ai-inventory", label: "AI Inventory", icon: Wand2, testid: "nav-ai-inv" },
  { to: "/forecasting", label: "AI Forecasting", icon: LineChart, testid: "nav-forecasting" },
  { to: "/finance", label: "Finance", icon: Landmark, testid: "nav-finance" },
  { to: "/developer", label: "Developer", icon: Code2, testid: "nav-developer" },
  { to: "/ai-ops", label: "AI Ops Center", icon: Activity, testid: "nav-ai-ops" },
  { to: "/connector-health", label: "Connector Health", icon: ShieldAlert, testid: "nav-connector-health" },
  { to: "/ai", label: "AI Insights", icon: Sparkles, testid: "nav-ai" },
];

const NETWORK_NAV = [
  { to: "/agencies", label: "Agencies", icon: Building2, testid: "nav-agencies" },
  { to: "/clients", label: "Clients", icon: Users2, testid: "nav-clients" },
  { to: "/brands", label: "Brands", icon: Package, testid: "nav-brands" },
  { to: "/warehouses", label: "Warehouses", icon: Warehouse, testid: "nav-warehouses" },
  { to: "/marketplaces", label: "Marketplaces", icon: ShoppingBag, testid: "nav-marketplaces" },
  { to: "/shopify", label: "Shopify", icon: Store, testid: "nav-shopify" },
];

const ADMIN_NAV = [
  { to: "/tenants", label: "Tenants", icon: Boxes, testid: "nav-tenants" },
  { to: "/plans", label: "Plans", icon: CreditCard, testid: "nav-plans" },
  { to: "/admin/finance/company-master", label: "Company Master", icon: Building2, testid: "nav-company-master" },
];

const BILLING_NAV = [
  { to: "/marketplace", label: "App Marketplace", icon: Store, testid: "nav-marketplace" },
  { to: "/billing", label: "Billing & Credits", icon: Wallet, testid: "nav-billing" },
  { to: "/training", label: "Training Center", icon: GraduationCap, testid: "nav-training" },
];
const COMMS_NAV = [
  { to: "/emails", label: "Emails & Leads", icon: Mail, testid: "nav-emails" },
];

const FOOTER_NAV = [
  { to: "/users", label: "Team", icon: UserCog, testid: "nav-users" },
  { to: "/audit", label: "Audit Logs", icon: ScrollText, testid: "nav-audit" },
  { to: "/settings", label: "Settings", icon: Settings, testid: "nav-settings" },
];

export default function Sidebar() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const loc = useLocation();

  const renderItem = (item) => (
    <NavLink
      key={item.to}
      to={item.to}
      data-testid={item.testid}
      end={item.to === "/dashboard"}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2 text-sm border-l-2 transition-colors ${
          isActive
            ? "border-primary bg-secondary text-foreground"
            : "border-transparent text-muted-foreground hover:text-foreground hover:bg-secondary/60"
        }`
      }
    >
      <item.icon className="w-4 h-4 shrink-0" strokeWidth={1.6} />
      <span>{item.label}</span>
    </NavLink>
  );

  return (
    <aside className="w-60 shrink-0 border-r border-border bg-card flex flex-col" data-testid="app-sidebar">
      <div className="px-4 py-5 border-b border-border">
        <a href="/dashboard" style={{
          display: "inline-block", background: "#fff", padding: "8px 12px",
          borderRadius: 8, textDecoration: "none",
        }}>
          <img
            src="https://customer-assets-cm19k8pv.emergentagent.net/job_scalable-fashion-oms/artifacts/es403apk_zaroori%20jini%20oms.png"
            alt="Zaroori Jini OMS"
            style={{ height: 32, width: "auto", objectFit: "contain", display: "block" }}
            data-testid="sidebar-oms-logo"
          />
        </a>
      </div>

      <nav className="py-3 flex-1 overflow-y-auto">
        <div className="label-mono px-3 pb-1">Fulfilment</div>
        {NAV.map(renderItem)}

        <div className="label-mono px-3 pt-4 pb-1">Intelligence</div>
        {OPS_NAV.map(renderItem)}

        <div className="label-mono px-3 pt-4 pb-1">Network</div>
        {NETWORK_NAV.map(renderItem)}

        {isSuper && (
          <>
            <div className="label-mono px-3 pt-4 pb-1">Platform</div>
            {ADMIN_NAV.map(renderItem)}
          </>
        )}

        <div className="label-mono px-3 pt-4 pb-1">Administration</div>
        {BILLING_NAV.map(renderItem)}

        {isSuper && (
          <>
            <div className="label-mono px-3 pt-4 pb-1">Communications</div>
            {COMMS_NAV.map(renderItem)}
          </>
        )}

        <div className="label-mono px-3 pt-4 pb-1">Governance</div>
        {FOOTER_NAV.map(renderItem)}
      </nav>

      <div className="p-3 border-t border-border">
        <div className="label-mono">Signed in as</div>
        <div className="text-sm font-medium truncate" data-testid="sidebar-user-name">{user?.name}</div>
        <div className="mt-1"><span className="role-badge" data-testid="sidebar-user-role">{user?.role}</span></div>
      </div>
    </aside>
  );
}
